import type { ApiRole, AuthUser } from "./api/types";
import { clearWorkContext, getWorkContext, setWorkContext } from "./workContext";

export const ACCOUNT_STORAGE_KEY = "safecheck-account-type";
export const AUTH_TOKEN_KEY = "safecheck-auth-token";
export const AUTH_USER_KEY = "safecheck-auth-user";

/**
 * Types de compte de l'interface. Les clés historiques sont conservées :
 * - "enterprise" = compte Assurance (rôle API insurer) ;
 * - "hospital" = compte Sinistre (rôle API claims).
 */
export type AccountType = "admin" | "auditor" | "hospital" | "enterprise";

export const accountRoutes: Record<AccountType, string> = {
  admin: "/dashboard/admin",
  auditor: "/dashboard/auditor",
  hospital: "/dashboard/hospital",
  enterprise: "/dashboard/enterprise",
};

const apiRoleToAccountMap: Record<ApiRole, AccountType> = {
  admin: "admin",
  super_admin: "admin",
  auditor: "auditor",
  claims: "hospital",
  insurer: "enterprise",
};

const accountToApiRoleMap: Record<AccountType, ApiRole> = {
  admin: "admin",
  auditor: "auditor",
  hospital: "claims",
  enterprise: "insurer",
};

export function apiRoleToAccountType(role: ApiRole): AccountType {
  return apiRoleToAccountMap[role] ?? "enterprise";
}

export function accountTypeToApiRole(type: AccountType): ApiRole {
  return accountToApiRoleMap[type];
}

export function getStoredAccountType(): AccountType | null {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(ACCOUNT_STORAGE_KEY);
  if (value === "admin" || value === "auditor" || value === "hospital" || value === "enterprise") {
    return value;
  }
  return null;
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getAuthUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function storeAuth(accessToken: string, user: AuthUser): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  localStorage.setItem(ACCOUNT_STORAGE_KEY, apiRoleToAccountType(user.role));

  // Assureur actif : celui du compte s'il n'y en a qu'un, sinon le précédent s'il reste accessible.
  const insurers = Array.isArray(user.insurers) ? user.insurers : [];
  const current = getWorkContext().insurerId;
  if (insurers.length === 1) {
    setWorkContext({ insurerId: insurers[0].id });
  } else if (!insurers.some((insurer) => insurer.id === current)) {
    setWorkContext({ insurerId: null });
  }
}

export function clearSession(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(ACCOUNT_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  clearWorkContext();
}

export function getAccountTypeRoute(type: AccountType): string {
  return accountRoutes[type];
}

export function isAuthenticated(): boolean {
  return Boolean(getAuthToken());
}

export function isSuperAdminUser(): boolean {
  return getAuthUser()?.role === "super_admin";
}

export function isPlatformAdminUser(): boolean {
  const role = getAuthUser()?.role;
  return role === "admin" || role === "super_admin";
}

/** Nom affiché dans le header / sidebar (entreprise ou hôpital en majuscules). */
export function getAccountDisplayLabel(): string | null {
  const user = getAuthUser();
  if (!user?.name) return null;
  const accountType = getStoredAccountType();
  if (accountType === "enterprise" || accountType === "hospital") {
    return user.name.toUpperCase();
  }
  return user.name;
}

/** Libellé affiché dans le header / sidebar (ex. SUPER ADMIN). */
export function getRoleDisplayLabel(): string {
  const user = getAuthUser();
  if (user?.role === "super_admin") return "SUPER ADMIN";
  const accountType = getStoredAccountType();
  const labels: Record<AccountType, string> = {
    admin: "ADMIN",
    auditor: "AUDITEUR",
    hospital: "HOPITAL",
    enterprise: "ENTREPRISE",
  };
  return accountType ? labels[accountType] : "ADMIN";
}

/** Mode démo : compte sélectionné sans token API. */
export function isDemoSession(): boolean {
  return !getAuthToken() && Boolean(getStoredAccountType());
}

export function hasDashboardAccess(): boolean {
  return isAuthenticated() || isDemoSession();
}
